import { getMatchdayNumber, getMatchdays } from './matchdays';

const number = (value) => value == null || typeof value === 'boolean' || String(value).trim() === ''
  ? null : Number.isInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;
const empty = () => ({ jugados: 0, ganados: 0, empatados: 0, perdidos: 0, golesFavor: 0, golesContra: 0, form: [] });
const fields = {
  jugados: 'jugados', ganados: 'ganados', empatados: 'empatados', perdidos: 'perdidos',
  golesFavor: 'goles_a_favor', golesContra: 'goles_en_contra',
};
const read = (team, field, suffix = '') => {
  const snake = suffix === 'Casa' ? 'casa' : 'fuera';
  const keys = suffix ? [`${field}${suffix}`, `${field.toLowerCase()}${suffix}`, `${fields[field]}_${snake}`]
    : [field, field.toLowerCase()];
  return keys.map((key) => number(team[key])).find((value) => value !== null) ?? null;
};
const agrees = (team, stats, suffix = '') => Object.keys(fields).every((field) => {
  const expected = read(team, field, suffix);
  return expected === null || expected === stats[field];
});
const matchDate = (match) => {
  const raw = String(match.fecha || '');
  const european = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  return Date.parse(european ? `${european[3]}-${european[2]}-${european[1]}` : raw);
};

export function completeVenueStandings(snapshots, calendar) {
  if (!Array.isArray(snapshots) || !calendar) return snapshots;
  const rounds = getMatchdays(calendar);
  return snapshots.map((snapshot, index) => {
    const limit = getMatchdayNumber(snapshot) ?? index + 1;
    const matches = new Map();
    const conflicts = new Set();
    rounds.forEach((round) => {
      const roundNumber = getMatchdayNumber(round);
      if (roundNumber === null || roundNumber > limit) return;
      round.partidos?.forEach((match) => {
        const home = match.codEquipoLocal ?? match.idEquipoLocal;
        const away = match.codEquipoVisitante ?? match.idEquipoVisitante;
        const scored = number(match.golesCasa);
        const conceded = number(match.golesVisitante);
        if (match.partidoJugado === false || home == null || away == null || scored === null || conceded === null) return;
        const key = `${roundNumber}:${home}:${away}`;
        const previous = matches.get(key);
        if (previous && (previous.scored !== scored || previous.conceded !== conceded)) {
          conflicts.add(`${home}:Casa`);
          conflicts.add(`${away}:Fuera`);
        }
        matches.set(key, {
          home: String(home), away: String(away), scored, conceded, round: roundNumber, date: matchDate(match),
        });
      });
    });
    const totals = new Map();
    const add = (id, venue, scored, conceded) => {
      if (!totals.has(id)) totals.set(id, { Casa: empty(), Fuera: empty() });
      const stats = totals.get(id)[venue];
      stats.jugados++;
      stats.golesFavor += scored;
      stats.golesContra += conceded;
      const result = scored > conceded ? 'V' : scored === conceded ? 'E' : 'P';
      stats[result === 'V' ? 'ganados' : result === 'E' ? 'empatados' : 'perdidos']++;
      stats.form.push(result);
    };
    const played = [...matches.values()];
    const dated = played.every((match) => Number.isFinite(match.date));
    played.sort((a, b) => (dated ? a.date - b.date : 0) || a.round - b.round).forEach((match) => {
      add(match.home, 'Casa', match.scored, match.conceded);
      add(match.away, 'Fuera', match.conceded, match.scored);
    });
    return { ...snapshot, clasificacion: snapshot.clasificacion?.map((team) => {
      if (team.codequipo == null) return team;
      const id = String(team.codequipo);
      const venues = totals.get(id) || { Casa: empty(), Fuera: empty() };
      const combined = Object.fromEntries(Object.keys(fields).map((field) =>
        [field, venues.Casa[field] + venues.Fuera[field]]));
      const overallCovered = read(team, 'jugados') === combined.jugados && agrees(team, combined);
      const enriched = { ...team };
      ['Casa', 'Fuera'].forEach((suffix) => {
        const stats = venues[suffix];
        if (conflicts.has(`${id}:${suffix}`)) return;
        const venueCovered = read(team, 'jugados', suffix) === stats.jugados;
        if ((!overallCovered && !venueCovered) || !agrees(team, stats, suffix)) return;
        Object.keys(fields).forEach((field) => {
          if (read(team, field, suffix) === null) enriched[`${field}${suffix}`] = stats[field];
        });
        stats.form.slice(-5).forEach((result, formIndex) => {
          const key = `racha_${formIndex + 1}_${suffix}`;
          const existing = team[key] ?? team[`racha_${formIndex + 1}_${suffix.toLowerCase()}`];
          if (!['G', 'V', 'W', 'E', 'D', 'P', 'L'].includes(String(existing).trim().toUpperCase())) {
            enriched[key] = result;
          }
        });
      });
      return enriched;
    }) };
  });
}
